// src/services/fundraiserApi.js
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

// Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Log EVERY request before it's sent
api.interceptors.request.use(
  (config) => {
    console.log(`📤 REQUEST: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    
    // Log the full request data
    if (config.data) {
      console.log('📤 REQUEST BODY:', config.data);
    }
    
    // Log headers (excluding sensitive ones if needed)
    const headersToLog = { ...config.headers };
    console.log('📤 REQUEST HEADERS:', headersToLog);
    
    return config;
  },
  (error) => {
    console.error('❌ REQUEST ERROR:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Log ALL responses, including errors
api.interceptors.response.use(
  (response) => {
    console.log(`📥 RESPONSE: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    console.log('📥 RESPONSE BODY:', response.data);
    return response;
  },
  (error) => {
    if (error.config) {
      // Log the request that caused the error
      console.log(`❌ FAILED REQUEST: ${error.config.method?.toUpperCase()} ${error.config.baseURL}${error.config.url}`);
      if (error.config.data) {
        console.log('❌ FAILED REQUEST BODY:', JSON.parse(error.config.data || '{}'));
      }
    }
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.log(`❌ ERROR RESPONSE: ${error.response.status} ${error.config?.url}`);
      console.log('❌ ERROR RESPONSE BODY:', error.response.data);
    } else if (error.request) {
      // The request was made but no response was received
      console.log('❌ NO RESPONSE RECEIVED:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('❌ REQUEST SETUP ERROR:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// ===== Fundraiser APIs =====

// 1️⃣ Save/Update Fundraiser Basic Info
export const saveFundraiserBasic = async (data, userId) => {
  try {
    const payload = {
      userId: userId || data.userId,
      title: data.title,
      goalAmount: typeof data.goalAmount === 'string'
        ? parseInt(data.goalAmount.replace(/\D/g, "") || "0")
        : data.goalAmount,
      purpose: data.purpose || "medical",
      basic: {
        relation: data.patientRelation,
        education: data.education,
        employment: data.employmentStatus,
        ref_source: data.heardAbout,
      },
    };
 
    const response = await api.post("/fundraisers/basic", payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || {
      success: false,
      message: error.message || 'Failed to save basic details'
    };
  }
};

// 2️⃣ Save/Update Patient Details
export const savePatientDetails = async (fundraiserId, data) => {
  try {
    const payload = {
      patientName: data.patientName,
      age: parseInt(data.patientAge),
      condition: data.medicalCondition,
      hospital: data.hospital,
      city: data.city,
      hospitalStatus: data.hospitalizationStatus,
    };

    const response = await api.post(`/fundraisers/${fundraiserId}/patient`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

// 3️⃣ Upload Document/File
export const uploadDocument = async (formData) => {
  try {
    if (!formData || !(formData instanceof FormData)) {
      throw new Error('Invalid FormData object');
    }

    const response = await axios.post(`${API_BASE_URL}/uploads/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: false,
    });
    
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { 
      success: false, 
      message: error.message || 'Failed to upload document',
      error: error.message 
    };
  }
};

// 4️⃣ Auto-generate Story
export const generateStory = async (fundraiserId) => {
  try {
    const response = await api.post(`/fundraisers/${fundraiserId}/auto-story`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to generate story');
  }
};

// 5️⃣ Submit Story (Final Step)
export const submitStory = async (fundraiserId, storyData) => {
  try {
    const payload = {
      title: storyData.title,
      storyHtml: storyData.storyHtml,
      add_documents: storyData.add_documents || storyData.addDocuments,
      consent_insurance: storyData.consent_insurance !== undefined ? storyData.consent_insurance : storyData.consentInsurance,
      agree_terms: storyData.agree_terms || storyData.agreeTerms,
    };

    const response = await api.post(`/fundraisers/${fundraiserId}/story`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

// 6️⃣ Get Fundraiser by ID
export const getFundraiserById = async (fundraiserId) => {
  try {
    const response = await api.get(`/fundraisers/${fundraiserId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

// 7️⃣ Submit Fundraiser (Final submission after story)
// 7️⃣ Submit Fundraiser (Final submission after story)
// 7️⃣ Submit Fundraiser (Final submission/publish)
// 7️⃣ Submit Fundraiser (Final submission/publish)
export const submitFundraiser = async (data) => {
  try {
    // ✅ FIX: Add null check
    if (!data) {
      throw new Error('No data provided to submitFundraiser');
    }
    
    // ✅ FIX: Check if userId exists
    if (!data.userId) {
      throw new Error('userId is required');
    }

    // ✅ FIX: Check if fundraiserId exists
    if (!data.fundraiserId) {
      throw new Error('fundraiserId is required');
    }

    const payload = {
      userId: data.userId
    };
    
    console.log('📤 Submitting fundraiser payload:', payload);
    console.log('📤 For fundraiser ID:', data.fundraiserId);
    
    // ✅ FIX: Use data.fundraiserId, not undefined fundraiserId variable
    const response = await api.post(`/fundraisers/${data.fundraiserId}/publish`, payload);
    return response.data;
  } catch (error) {
    console.error('❌ submitFundraiser error:', error.response?.data || error.message);
    throw error.response?.data || { 
      success: false, 
      message: error.message || 'Failed to submit fundraiser' 
    };
  }
};

export default {
  saveFundraiserBasic,
  savePatientDetails,
  uploadDocument,
  generateStory,
  submitStory,
  getFundraiserById,
  submitFundraiser,
};